import { fieldNames } from '../../../../shared/configs/acceptanceForm';
import { formatDate } from '../../../../shared/packages/dateTime';

const {
  main: { configId: configIdName, desks: desksName },
  desk: {
    name: deskIdName,
    workingDays: workingDaysName,
    scheduleSettings: scheduleSettingsName,
    dateRanges: dateRangesName,
    isCommonSchedules: isCommonSchedulesName,
  },
  deskScheduleSetting: { intervals: intervalsName },
  scheduleSettingInterval: {
    intervalId: intervalIdName,
    active: intervalActiveName,
  },
} = fieldNames;

export class Collection {
  constructor(settings = []) {
    this._collection = settings;
    this._init(settings);
  }

  _init(settings) {
    this.configOptions = [];
    this._immutableConfigs = {};

    settings.forEach((setting, index) => {
      const configId = setting[configIdName];

      this.configOptions.push({
        key: configId,
        value: configId,
        label: `Конфигурация ${index + 1}`,
      });

      this._immutableConfigs[configId] = this._createImmutableDesks(
        setting[desksName]
      );
    });
  }

  _createImmutableIntervalIds(intervals) {
    const result = new Set();

    intervals.forEach((interval) => {
      if (interval[intervalActiveName]) {
        result.add(this._getIntervalId(interval[intervalIdName]));
      }
    });

    return result;
  }

  _getIntervalId(data) {
    return typeof data === 'object' ? data.context.id : data;
  }

  _createImmutableUniqueDates(dates) {
    return new Set(
      dates?.map((date) => {
        return `${formatDate(date.fromDate)}-${formatDate(date.toDate)}`;
      })
    );
  }

  _createImmutableIntervals(desk) {
    const isCommonSchedules = desk[isCommonSchedulesName];
    const schedules = desk[scheduleSettingsName];

    if (isCommonSchedules) {
      return this._createImmutableIntervalIds(schedules[intervalsName]);
    }

    const dayIds = desk[workingDaysName];

    return dayIds.reduce((acc, id) => {
      acc[id] = this._createImmutableIntervalIds(
        schedules[`${id}str`]?.[intervalsName] ?? schedules[intervalsName]
      );

      return acc;
    }, this._createObjectWithSize(dayIds.length));
  }

  _createImmutableDesks(desks) {
    return desks.reduce((acc, desk) => {
      acc[desk[deskIdName].key] = {
        isCommonSchedules: desk[isCommonSchedulesName],
        days: new Set(desk[workingDaysName]),
        intervals: this._createImmutableIntervals(desk),
        uniqueRanges: this._createImmutableUniqueDates(desk[dateRangesName]),
      };
      return acc;
    }, this._createObjectWithSize(desks.length));
  }

  getImmutabilityChecks(configId) {
    if (!configId) return;

    return {
      desk: this._isDeskImmutable.bind(this, configId),
      uniqueRange: this._isDateRangeImmutable.bind(this, configId),
      day: this._isDayImmutable.bind(this, configId),
      interval: this._isIntervalImmutable.bind(this, configId),
    };
  }

  _isDeskImmutable(configId, deskId) {
    return Boolean(this._immutableConfigs[configId]?.[deskId]);
  }

  _isDayImmutable(configId, deskId, dayId) {
    const desk = this._immutableConfigs[configId]?.[deskId];

    if (!desk) {
      return false;
    }

    if (desk.isCommonSchedules && dayId == null) {
      return true;
    }

    return desk.days.has(dayId);
  }

  _isIntervalImmutable(configId, deskId, dayId, intervalId) {
    const intervals = this._immutableConfigs[configId]?.[deskId]?.intervals;

    if (!intervals) {
      return false;
    }

    const intervalIdPayload = this._getIntervalId(intervalId);

    if (intervals instanceof Set) {
      return intervals.has(intervalIdPayload);
    }

    return Boolean(intervals[dayId]?.has(intervalIdPayload));
  }

  _isDateRangeImmutable(configId, deskId, rangeStr) {
    return Boolean(
      this._immutableConfigs[configId]?.[deskId]?.uniqueRanges?.has(rangeStr)
    );
  }

  getConfigById(configId) {
    return this._collection.find((config) => config[configIdName] === configId);
  }

  getFirstConfig() {
    return this._collection[0];
  }

  _createObjectWithSize(size) {
    return Object.defineProperty({}, 'size', {
      value: size,
      writable: false,
      enumerable: false,
      configurable: false,
    });
  }

  hasChanged(values) {
    const initial = this._immutableConfigs[values[configIdName]];

    const desks = values[desksName];

    if (initial.size < desks.length) {
      return true;
    }

    for (const desk of desks) {
      const deskId = desk[deskIdName].key;

      const { days, uniqueRanges, intervals } = initial[deskId];

      if (uniqueRanges.size < desk[dateRangesName].length) {
        return true;
      }

      const dayIds = desk[workingDaysName];

      if (days.size < dayIds.length) {
        return true;
      }

      if (intervals instanceof Set) {
        const schedule = desk[scheduleSettingsName];
        const currentIntervals = schedule[intervalsName].filter(
          (interval) => interval[intervalActiveName]
        );

        if (intervals.size < currentIntervals.length) {
          return true;
        }
      } else {
        for (const dayId of dayIds) {
          const daySchedule = desk[scheduleSettingsName][`${dayId}str`];
          const currentIntervals = daySchedule[intervalsName].filter(
            (interval) => interval[intervalActiveName]
          );

          if (intervals[dayId].size < currentIntervals.length) {
            return true;
          }
        }
      }
    }

    return false;
  }

  get size() {
    return this._collection.length;
  }
}
